import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  getIncomers,
  getOutgoers,
  getConnectedEdges,
  useReactFlow,
  addEdge,
} from "@xyflow/react";
import {
  useState,
  createContext,
  useContext,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  PhoneIcon,
  ChatBubbleLeftIcon,
  ClockIcon,
  BoltIcon,
  WrenchIcon,
  WrenchScrewdriverIcon,
  CheckBadgeIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";
import Drawer from "V2.0/components/Drawer";
import { Badge, BadgeButton } from "V2.0/common/badge";
import { FaWhatsapp } from "react-icons/fa";
import { NODE_SETTINGS } from "./nodesSettings";
import { nanoid } from "nanoid";
import { useBuilder } from "../Builder";
import useLayout from "./useLayout";
import useAutoLayout from "./useAutoLayout";
import { StartEdge, CustomEdge } from "./edges";
import { CustomNode } from "./nodes/index1";
import { PlaceholderNode } from "./nodes/Placeholder";
import { SimpleEdge } from "./edges/SimpleEdge";
import { AnalysisPath } from "./AnalysisPath";

import { defaultAssistantNode } from "./models/assistantNode";
import {
  defaultAnalysisNode,
  defaultKeywordAnalysisNode,
} from "./models/analysisNode";
import {
  defaultArchiveConversationNode,
  defaultEndNode,
  defaultWebhookNotificationNode,
} from "./models/endNode";
import { updatePipeline } from "../service";
import { message } from "antd";
import { TbWebhook } from "react-icons/tb";
import "@xyflow/react/dist/style.css";

const NODE_STATUS = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  FAILED: "failed",
  SKIPPED: "skipped",
  WAITING_LONG_DELAY: "waiting_long_delay",
  BLOCKED: "blocked",
};

export const NODE_STATUS_COLORS = {
  [NODE_STATUS.PENDING]: "yellow",
  [NODE_STATUS.IN_PROGRESS]: "orange",
  [NODE_STATUS.COMPLETED]: "green",
  [NODE_STATUS.FAILED]: "red",
  [NODE_STATUS.SKIPPED]: "pink",
  [NODE_STATUS.WAITING_LONG_DELAY]: "orange",
  [NODE_STATUS.BLOCKED]: "red",
};

const nodeTypes = {
  custom: CustomNode,
  placeholder: PlaceholderNode,
};

const edgeTypes = {
  customEdge: CustomEdge,
  simpleEdge: SimpleEdge,
};

const defaultNodes = [
  {
    id: "placeholder",
    type: "placeholder",
    position: { x: 0, y: 0 },
  },
];

const defaultEdges = [];

function getAvailableChoices(output, choices, node, nodes, edges) {
  const outgoers = edges.filter((edge) => edge.source === node.id);
  const outgoersChoices = outgoers.map((outgoer) => {
    const { output, value } = outgoer.data;
    return value;
  });
  const availableChoices = choices.filter(
    (choice) => !outgoersChoices.includes(choice)
  );
  console.log({
    availableChoices,
    outgoersChoices,
    node,
    output,
    choices,
    outgoers,
  });
  return availableChoices.map((choice) => {
    return {
      outputVariableName: output,
      choice,
    };
  });
}

function generateNodesAndEdges(workflow) {
  const { nodes, edges } = workflow || [];

  if (nodes.length === 0) {
    return {
      initialNodes: defaultNodes,
      initialEdges: defaultEdges,
    };
  } else {
    const transformedNodes = nodes.map((node, idx) => {
      const { nodeId, ...restOfTheNode } = node;
      return {
        id: nodeId,
        data: {
          ...restOfTheNode,
          status: [],
        },
        type: "custom",
        position: { x: 0, y: 0 },
      };
    });

    const firstNode = nodes[0];

    const placeholderNodeEdge = {
      id: `placeholder->${firstNode.nodeId}`,
      source: "placeholder",
      target: firstNode.nodeId,
      type: "smoothstep",
    };

    const transformedEdges = edges.map((edge) => {
      const { from, to, condition } = edge;
      const [status, value] = condition.split("=");

      return {
        id: `${from}->${to}`,
        source: from,
        target: to,
        type: "customEdge",
        data: {
          output: status,
          value,
        },
      };
    });

    return {
      initialNodes: transformedNodes,
      initialEdges: transformedEdges,
    };
  }
}

function getSubtreeNodes(graph, nodeId) {
  const visited = new Set();
  const stack = [nodeId];
  const subtreeNodeIds = [];

  // Traverse the graph in DFS manner to get all node IDs in the subtree
  while (stack.length > 0) {
    const node = stack.pop();

    if (!visited.has(node)) {
      visited.add(node); // Mark the node as visited
      subtreeNodeIds.push(node); // Add nodeId to the list of nodes in the subtree

      // Ensure that each node's adjacency list is an array (even if empty)
      const neighbors = graph[node] || []; // Default to empty array if no neighbors exist

      // Push all connected neighbors (children) to the stack
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          stack.push(neighbor); // Add the neighbor to the stack to visit
        }
      }
    }
  }

  return subtreeNodeIds; // Return the actual node objects in the subtree
}

// Function to remove a subtree from nodes and edges
function removeSubtree(nodes, edges, rootNodeId) {
  // Step 1: Build the adjacency list (graph representation)
  const graph = {};
  nodes.forEach((node) => {
    graph[node.id] = [];
  });
  edges.forEach(({ source, target }) => {
    graph[source].push(target);
  });

  // Step 2: Find all nodes in the subtree rooted at rootNodeId
  const subtreeNodes = getSubtreeNodes(graph, rootNodeId);

  // Step 3: Remove all nodes in the subtree from the nodes array
  const updatedNodes = nodes.filter((node) => !subtreeNodes.includes(node.id));

  // Step 4: Remove all edges involving any node in the subtree
  const updatedEdges = edges.filter(({ source, target }) => {
    return !subtreeNodes.includes(source) && !subtreeNodes.includes(target);
  });

  return {
    updatedNodes,
    updatedEdges,
  };
}

const WorkflowBuilderContext = createContext({});
export const useWorkflowBuilder = () => {
  return useContext(WorkflowBuilderContext);
};

export function WorkflowBuilders(props) {
  const { workflow, projectId, onSave } = props;
  const { initialNodes, initialEdges } = generateNodesAndEdges(workflow);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [panel, setPanel] = useState({ isOpen: false, data: null });
  const { notifyChange, transformNodes, transformEdges } = useBuilder();
  const { getNode, setCenter, getViewport } = useReactFlow();
  const triggerType = useRef(null);

  const openPanel = (data) => setPanel({ isOpen: true, ...data });
  const closePanel = () => {
    setPanel({ isOpen: false });
  };

  useLayout();

  const updatedNodes = useMemo(() => {
    return nodes.map((node) => {
      if (!Boolean(triggerType.current) && node?.data?.nodeType === "trigger") {
        const { communicationBinding, communicationType } = node.data.settings;
        if (communicationType) {
          triggerType.current = communicationType;
        } else {
          if (communicationBinding.length > 0) {
            const { channelType } = communicationBinding[0];
            triggerType.current = channelType;
          } else {
            triggerType.current = "whatsapp";
          }
        }
      }
      return {
        ...node,
        data: {
          ...node.data,
          onDelete: () => {
            handleDeleteNode(node);
          },
        },
      };
    });
  }, [nodes]);

  const updatedEdges = useMemo(
    () =>
      edges.map((edge) => {
        return {
          ...edge,
          data: {
            ...edge.data,
          },
        };
      }),
    [edges]
  );

  const handleAddNode = (node) => {
    notifyChange();

    const isStartNode = panel.data.isStartNode;
    const status = panel.data.status;
    const id = panel.data.id;

    if (isStartNode) {
      const newNode = {
        ...node,
        data: { ...node.data, status: [] },
        id: nanoid(),
        position: {
          x: 0,
          y: 0,
        },
      };
      updatePipeline({
        workspaceId: projectId,
        pipelineId: workflow._id,
        payload: {
          nodes: [
            {
              nodeId: newNode.id,
              nodeType: "trigger",
              settings: {
                name: node.data.settings.name,
                description: node.data.settings.description,
                communicationType: node.data.settings.communicationType,
                communicationBinding: [],
              },
            },
          ],
        },
      }).then((response) => {
        if (response.status === 200) {
          const nodes = response.data.nodes;
          const _node = nodes[0];
          const _newNode = {
            ...newNode,
            data: {
              ...newNode.data,
              _id: _node._id,
            },
          };
          setNodes([_newNode]);
          openPanel({
            mode: "Configure",
            data: _newNode,
          });
        }
      });

      return;
    }

    const newNode = {
      ...node,
      data: {
        ...node.data,
        status: [],
      },
      id: nanoid(),
      position: {
        x: panel.data.position.x,
        y: panel.data.position.y + 200,
      },
    };

    let newEdge;

    // if (panel.data.data.nodeType === "analysis") {
    newEdge = {
      id: `${id}->${newNode.id}`,
      source: id,
      target: newNode.id,
      type: "customEdge",
      data: {
        output: panel.data.output,
        value: panel.data.value,
      },
    };
    // } else {
    //   newEdge = {
    //     id: `${id}->${newNode.id}`,
    //     source: id,
    //     target: newNode.id,
    //     type: "customEdge",
    //     data: {
    //       output,
    //       value,
    //     },
    //   };
    // }

    setEdges((edges) => {
      const newEdges = [...edges, newEdge];
      return newEdges;
    });

    setNodes((nodes) => {
      const updatedNodes = nodes.map((node) => {
        if (node.id === id) {
          const newNode = {
            ...node,
            data: {
              ...node.data,
              status: [...node.data.status, status],
            },
          };
          return newNode;
        } else {
          return node;
        }
      });
      const newNodes = [...updatedNodes, newNode];
      return newNodes;
    });

    setPanel({
      isOpen: true,
      mode: "Configure",
      data: newNode,
    });
  };

  const handleMultipleDelete = (deletedNode) => {
    const { updatedNodes, updatedEdges } = removeSubtree(
      nodes,
      edges,
      deletedNode.id
    );

    if (updatedNodes.length === 0) {
      setNodes(initialNodes);
    } else {
      setNodes(updatedNodes);
    }

    setEdges(updatedEdges);
  };

  function deleteTriggerNode(deletedNode) {
    const outgoers = getOutgoers(deletedNode, nodes, edges); // outgoers are the nodes which can be reached from the deleted node
    const connectedEdges = getConnectedEdges([deletedNode], edges); // there will be an edge in which the deleted node is a target and another where
    const remainingEdges = edges.filter(
      (edge) => !connectedEdges.includes(edge)
    ); // remove all the edges which includes the deleted node in its source/target
    const updatedNodes = nodes.reduce((acc, node) => {
      if (node.id != deletedNode.id) {
        return [...acc, node];
      }

      return acc;
    }, []); // re
    const createdEdges = outgoers.map(({ id: target }) => {
      return {
        id: `placeholder->${target}`,
        source: "placeholder",
        target,
        type: "smoothstep",
      };
    });

    setNodes([...defaultNodes, ...updatedNodes]);
    setEdges([...createdEdges, ...remainingEdges]);
  }

  const handleDeleteNode = (deletedNode) => {
    if (deletedNode.data.nodeType === "trigger") {
      deleteTriggerNode(deletedNode);
      return;
    }

    const incomers = getIncomers(deletedNode, nodes, edges); // incomers are the nodes which connect to the deleted node
    const outgoers = getOutgoers(deletedNode, nodes, edges); // outgoers are the nodes which can be reached from the deleted node
    const connectedEdges = getConnectedEdges([deletedNode], edges); // there will be an edge in which the deleted node is a target and another where
    // this node is a source.

    if (outgoers.length > 1) {
      handleMultipleDelete(deletedNode);
      return;
    }

    const remainingEdges = edges.filter(
      (edge) => !connectedEdges.includes(edge)
    ); // remove all the edges which includes the deleted node in its source/target

    const createdEdges = incomers.flatMap((incomer) => {
      const { id: source } = incomer;
      const { data } = connectedEdges[0];
      return outgoers.map(({ id: target }) => {
        return {
          id: `${source}->${target}`,
          source,
          target,
          type: "customEdge",
          data,
        };
      });
    });

    const edgeConnectedToDeletedNode = connectedEdges.find(
      (edge) => edge.target === deletedNode.id
    );
    const updatedNodes = nodes.reduce((acc, node) => {
      if (node.id === edgeConnectedToDeletedNode?.source) {
        const newNode = {
          ...node,
          data: {
            ...node.data,
            status: node.data.status.filter(
              (status) => status != edgeConnectedToDeletedNode.data.status
            ),
          },
        };
        return [...acc, newNode];
      }

      if (node.id != deletedNode.id) {
        return [...acc, node];
      }

      return acc;
    }, []); // removing the deleted node

    const updatedEdges = [...remainingEdges, ...createdEdges];

    if (updatedNodes.length === 0) {
      setNodes(initialNodes);
    } else {
      setNodes(updatedNodes);
    }
    setEdges(updatedEdges);
  };

  const handleNodeClick = (e, node) => {
    if (node.id === "placeholder") {
      openPanel({ mode: "add", data: { isStartNode: true } });
      return;
    }

    openPanel({
      mode: "Configure",
      data: node,
    });
  };

  return (
    <WorkflowBuilderContext.Provider
      value={{
        openPanel,
        isPanelOpen: panel.isOpen,
        pipelineId: workflow._id,
        workspaceId: projectId,
      }}
    >
      <ReactFlow
        nodes={updatedNodes}
        edges={updatedEdges}
        onNodeClick={handleNodeClick}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        // onConnect={(connection) => {
        //   console.log({ connection });
        //   const sourceNode = getNode(connection.source);
        //   const targetNode = getNode(connection.target);
        //   console.log({ sourceNode, targetNode });
        //   if (sourceNode.data.nodeType === "trigger") {
        //     if (targetNode.data.nodeType !== "assistant") {
        //       message.error(
        //         `${targetNode.data.nodeType} is not allowed after ${sourceNode.data.nodeType}`
        //       );
        //       return;
        //     }
        //     const newEdge = {
        //       id: `${connection.source}->${connection.target}`,
        //       source: connection.source,
        //       target: connection.target,
        //       data: {
        //         output: "status",
        //         value: "completed"
        //       },
        //       type: "customEdge",
        //     };
        //     setEdges((prevEdges) => addEdge(newEdge, prevEdges));
        //   }
        // }}
        // onEdgeClick={handleEdgeClick}
        nodesDraggable={false}
        fitView
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        proOptions={{ hideAttribution: true }}
        minZoom={0.5}
        zoomOnScroll
        panOnScroll
      >
        <Background />
        {/* <Controls /> */}
      </ReactFlow>
      {panel.isOpen && (
        <Drawer open={panel.isOpen} onClose={closePanel} title="Configuration">
          {panel.mode === "add" && (
            <div className="flex flex-col gap-y-8">
              {panel.data.isStartNode && (
                <div className="flex flex-col gap-y-2">
                  <p className="text-sm font-semibold text-gray-500">
                    Triggers
                  </p>
                  <div className="flex flex-col gap-y-2">
                    <div
                      onClick={() =>
                        handleAddNode({
                          data: {
                            nodeType: "trigger",
                            settings: {
                              name: "Inbound Whatsapp Trigger",
                              description: "Inbound Whatsapp Trigger",
                              communicationType: "whatsapp",
                              communicationBinding: [
                                {
                                  channelType: "whatsapp",
                                  isActive: true,
                                  identifier: "",
                                },
                              ],
                            },
                            isStartNode: true,
                            status: [],
                          },
                          selected: true,
                          type: "custom",
                        })
                      }
                      className="flex items-center border rounded px-2 py-1 gap-x-2 cursor-pointer hover:bg-gray-200"
                    >
                      <BoltIcon className="size-4" /> Inbound Whatsapp Trigger
                    </div>
                    <div
                      onClick={() =>
                        handleAddNode({
                          data: {
                            nodeType: "trigger",
                            settings: {
                              name: "Inbound SMS Trigger",
                              description: "Inbound SMS Trigger",
                              communicationType: "sms",
                              communicationBinding: [
                                {
                                  channelType: "sms",
                                  isActive: true,
                                  identifier: "",
                                },
                              ],
                            },
                            isStartNode: true,
                            status: [],
                          },
                          selected: true,
                          type: "custom",
                        })
                      }
                      className="flex items-center border rounded px-2 py-1 gap-x-2 cursor-pointer hover:bg-gray-200"
                    >
                      <BoltIcon className="size-4" /> Inbound SMS Trigger
                    </div>
                  </div>
                </div>
              )}
              {/* <div className="flex flex-col gap-y-2">
                <p className="text-sm font-semibold text-gray-500">
                  Communication
                </p>
                <div className="flex flex-col gap-y-2">
                  <div
                    onClick={() =>
                      handleAddNode({
                        data: {
                          nodeType: "communication",
                          communicationType: "call",
                          name: "Call",
                          description: "",
                          settings: {
                            message: "",
                          },
                          status: [],
                        },
                        selected: true,
                        type: "custom",
                      })
                    }
                    className="flex items-center border rounded px-2 py-1 gap-x-2 cursor-pointer hover:bg-gray-200"
                  >
                    <PhoneIcon className="size-4" /> Call
                  </div>
                  <div
                    onClick={() =>
                      handleAddNode({
                        data: {
                          nodeType: "communication",
                          communicationType: "sms",
                          name: "Sms",
                          description: "",
                          settings: {
                            message: "",
                          },
                          status: [],
                        },
                        selected: true,
                        type: "custom",
                      })
                    }
                    className="flex items-center border rounded px-2 py-1 gap-x-2 cursor-pointer hover:bg-gray-200"
                  >
                    <ChatBubbleLeftIcon className="size-4" /> SMS
                  </div>
                  <div
                    onClick={() =>
                      handleAddNode({
                        data: {
                          nodeType: "communication",
                          communicationType: "whatsapp",
                          name: "Whatsapp",
                          description: "",
                          settings: {
                            template: "",
                          },
                          status: [],
                        },
                        selected: true,
                        type: "custom",
                      })
                    }
                    className="flex items-center border rounded px-2 py-1 gap-x-2 cursor-pointer hover:bg-gray-200"
                  >
                    <FaWhatsapp className="size-4" /> WhatsApp
                  </div>
                </div>
              </div> */}
              {/* <div className="flex flex-col gap-y-2">
                <p className="text-sm font-semibold text-gray-500">Timers</p>
                <div className="flex flex-col gap-y-2">
                  <div
                    onClick={() =>
                      handleAddNode({
                        data: {
                          nodeType: "delay",
                          name: "Delay",
                          description: "",
                          settings: {
                            delaySeconds: "",
                          },
                          status: [],
                        },
                        selected: true,
                        type: "custom",
                      })
                    }
                    className="flex items-center border rounded px-2 py-1 gap-x-2 cursor-pointer hover:bg-gray-200"
                  >
                    <ClockIcon className="size-4" /> Delay
                  </div>
                </div>
              </div> */}
              {panel?.data?.data?.nodeType === "assistant" && (
                <div className="flex flex-col gap-y-2">
                  <p className="text-sm font-semibold text-gray-500">
                    Decision
                  </p>
                  <div className="flex flex-col gap-y-2">
                    <div
                      onClick={() => {
                        handleAddNode({
                          data: {
                            ...defaultKeywordAnalysisNode,
                            status: [],
                          },
                          selected: true,
                          type: "custom",
                        });
                      }}
                      className="flex items-center border rounded px-2 py-1 gap-x-2 cursor-pointer hover:bg-gray-200"
                    >
                      <ClockIcon className="size-4" /> Keyword Analysis
                    </div>
                  </div>
                </div>
              )}
              {!panel?.data?.isStartNode && (
                <div className="flex flex-col gap-y-2">
                  <p className="text-sm font-semibold text-gray-500">
                    Assistants
                  </p>
                  <div className="flex flex-col gap-y-2">
                    <div
                      onClick={() =>
                        handleAddNode({
                          data: {
                            ...defaultAssistantNode,
                            status: [],
                          },
                          selected: true,
                          type: "custom",
                        })
                      }
                      className="flex items-center border rounded px-2 py-1 gap-x-2 cursor-pointer hover:bg-gray-200"
                    >
                      <WrenchScrewdriverIcon className="size-4" /> Assign
                      Assistant
                    </div>
                  </div>
                </div>
              )}
              {!panel?.data?.isStartNode &&
                panel?.data?.data?.nodeType != "trigger" && (
                  <div className="flex flex-col gap-y-2">
                    <p className="text-sm font-semibold text-gray-500">End</p>
                    <div className="flex flex-col gap-y-2">
                      <div
                        onClick={() =>
                          handleAddNode({
                            data: {
                              ...defaultEndNode,
                              status: [],
                            },
                            selected: true,
                            type: "custom",
                          })
                        }
                        className="flex items-center border rounded px-2 py-1 gap-x-2 cursor-pointer hover:bg-gray-200"
                      >
                        <CheckBadgeIcon className="size-4" /> Completed
                      </div>
                    </div>
                    <div className="flex flex-col gap-y-2">
                      <div
                        onClick={() =>
                          handleAddNode({
                            data: {
                              ...defaultArchiveConversationNode,
                              status: [],
                            },
                            selected: true,
                            type: "custom",
                          })
                        }
                        className="flex items-center border rounded px-2 py-1 gap-x-2 cursor-pointer hover:bg-gray-200"
                      >
                        <ArchiveBoxIcon className="size-4" /> Archive
                        Conversation
                      </div>
                    </div>
                    <div className="flex flex-col gap-y-2">
                      <div
                        onClick={() =>
                          handleAddNode({
                            data: {
                              ...defaultWebhookNotificationNode,
                              status: [],
                            },
                            selected: true,
                            type: "custom",
                          })
                        }
                        className="flex items-center border rounded px-2 py-1 gap-x-2 cursor-pointer hover:bg-gray-200"
                      >
                        <TbWebhook className="size-4" /> Webhook Notification
                      </div>
                    </div>
                  </div>
                )}
            </div>
          )}
          {panel.mode === "Configure" && (
            <div className="flex flex-col">
              {NODE_SETTINGS[panel.data.data.nodeType]({
                communicationType: panel.data?.data?.communicationType,
                pipelineType:
                  panel.data?.data?.settings?.communicationType ||
                  triggerType.current,
                node: panel.data,
                options: panel?.options,
                setNodes,
                closePanel,
                projectId,
                workspaceId: projectId,
                notifyChange,
                setEdges
              })}
            </div>
          )}
          {panel.mode === "addPath" && (
            <div className="flex flex-col gap-y-4">
              <div className="flex flex-col gap-y-8">
                <p className="text-sm font-semibold text-gray-800">
                  Next Step Conditions
                </p>
                {
                  // panel?.data?.data?.nodeType === "assistant" ? (
                  //   <div>
                  //     {panel?.data?.data?.settings.monitoringSettings.map(
                  //       (setting) => {
                  //         return (
                  //           <div
                  //             onClick={() => {
                  //               openPanel({
                  //                 mode: "add",
                  //                 data: {
                  //                   ...panel.data,
                  //                   output: "status",
                  //                   value: setting.statusToSet,
                  //                 },
                  //               });
                  //             }}
                  //             className="w-full border p-2 rounded-lg flex justify-between gap-x-2 mb-4 cursor-pointer hover:bg-gray-100"
                  //           >
                  //             <p className="text-sm">
                  //               {setting.triggerDescription}
                  //             </p>
                  //             {/* <div>
                  //               <Badge
                  //                 color={NODE_STATUS_COLORS[setting.statusToSet]}
                  //               >
                  //                 {setting.statusToSet}
                  //               </Badge>
                  //             </div> */}
                  //           </div>
                  //         );
                  //       }
                  //     )}
                  //   </div>
                  // ) :
                  panel?.data?.data?.nodeType === "analysis" ? (
                    <div>
                      {panel.data.data.settings.analysisType ===
                      "param_extraction" ? (
                        <div className="flex flex-col gap-y-2">
                          {getAvailableChoices(
                            panel?.data?.data?.settings?.outputVariableName,
                            panel?.data?.data?.settings?.parameter
                              ?.parameterChoices,
                            panel.data,
                            nodes,
                            edges
                          ).map(({ outputVariableName, choice }) => {
                            return (
                              <div
                                className="border p-2 rounded-lg cursor-pointer hover:bg-gray-100"
                                onClick={() => {
                                  openPanel({
                                    mode: "add",
                                    data: {
                                      ...panel.data,
                                      output:
                                        panel.data.data.settings
                                          .outputVariableName,
                                      value: choice,
                                      // output: "status",
                                      // value: "completed",
                                    },
                                  });
                                }}
                              >
                                <p className="text-sm">{`${outputVariableName}=${choice}`}</p>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div></div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-y-4">
                      {Object.values(NODE_STATUS)
                        .filter(
                          (status) => !panel.data.data.status.includes(status)
                        )
                        .map((status) => {
                          return (
                            <BadgeButton
                              color={NODE_STATUS_COLORS[status]}
                              id={status}
                              onClick={() => {
                                openPanel({
                                  mode: "add",
                                  data: {
                                    ...panel.data,
                                    status,
                                  },
                                });
                              }}
                            >
                              {status}
                            </BadgeButton>
                          );
                        })}
                    </div>
                  )
                }
              </div>
            </div>
          )}
        </Drawer>
      )}
    </WorkflowBuilderContext.Provider>
  );
}
